import * as XLSX from "xlsx";
import { SUBCATS } from "@/lib/calc";
import { statusOf, leaderboard } from "@/lib/status";
import { partsOf } from "@/lib/spot";
import { verifyCount, paymentStateLabel } from "@/lib/payments";

export const catLabel = (i) => [i.spot && "Spot", i.revisit && "Revisit"].filter(Boolean).join(" + ") || "—";
export const subLabel = (s = {}) => SUBCATS.filter((x) => s[x.key]).map((x) => x.label).join(", ") || "—";
export const localDay = (iso) => new Date(iso).toLocaleDateString("en-CA");

const toRow = (r) => {
  const i = r.inputs, c = { rows1: [{}, {}], rows2: [{}, {}, {}], quarterly: [], ...r.computed };
  return {
    "Receipt No": r.receipt_no,
    "Created On": new Date(r.created_at).toLocaleString("en-IN"),
    "Date of Enquiry": i.enquiryDate,
    "Child Name": i.childName,
    "Parent Name": i.parentName,
    Contact: i.contact,
    Email: i.email || "",
    Grade: i.grade,
    Category: catLabel(i),
    "Sub Category": subLabel(i.sub),
    "Admission Fee": c.rows1[0].base,
    "Admission Fee Discount": c.rows1[0].disc,
    "Form Fee": c.rows1[1].base,
    "Admission & Form Fee": c.oneTimeTotal,
    "Tuition Fee": c.rows2[0].base,
    "Tuition Fee Discount": c.rows2[0].disc,
    "Term Fee": c.rows2[1].base,
    CASP: c.rows2[2].base,
    "Annual Fee": c.annualFee,
    "School Fee after Discounts": c.schoolFeeAfterDisc,
    "Instalment 1": c.quarterly[0],
    "Instalment 2": c.quarterly[1],
    "Instalment 3": c.quarterly[2],
    "Instalment 4": c.quarterly[3],
    "Monthly Start": c.startMonth,
    "Monthly Amount": Math.round(c.monthly * 100) / 100,
    "No. of Months": c.months,
    "Total Discount": c.totalDiscount,
    "Adm Counsellor": i.counselorName,
    Status: statusOf(r).label,
    "Payment State": paymentStateLabel(verifyCount(r)),
    "Part-Payments Total": partsOf(r).reduce((t, x) => t + Number(x.amount || 0), 0) || "",
    "Part-Payment Modes": partsOf(r).map((x) => x.mode).join(", "),
    "Part-Payment Dates": partsOf(r).map((x) => x.date).join(", "),
    "Follow-up Date": r.follow_up_date || "",
    "Follow-up Note": r.follow_up_note || "",
    "Referral Name": r.inputs.referralName || "",
    "Referral Mobile No.": r.inputs.referralMobile || "",
    "Sibling Full Name": r.inputs.siblingName || "",
    "Sibling Grade": r.inputs.siblingGrade || "",
    "Form Fee Paid": r.form_fee?.amount ?? "",
    "Form Fee Mode": r.form_fee?.mode || "",
    "Form Fee Reference": r.form_fee?.reference || "",
    "Form Fee Date": r.form_fee?.date || "",
    "Payment Mode": r.payment?.mode || "",
    "Payment Reference": r.payment?.reference || "",
    "Amount Paid": r.payment?.amount ?? "",
    "Payment Date": r.payment?.date || "",
    Remarks: i.remarks,
  };
};

const addSheet = (wb, rows, name) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = Object.keys(rows[0]).map((k) => ({ wch: Math.max(12, k.length + 2) }));
  XLSX.utils.book_append_sheet(wb, ws, name);
};

const totals = (list) => ({
  Receipts: list.length,
  "Due at Admission": list.reduce((s, r) => s + r.computed.atAdmission, 0),
  "School Fee after Discounts": list.reduce((s, r) => s + r.computed.schoolFeeAfterDisc, 0),
  "Total Discount": list.reduce((s, r) => s + r.computed.totalDiscount, 0),
});

const groupBy = (list, key) => list.reduce((m, r) => ((m[key(r)] ||= []).push(r), m), {});

export const spotExceptions = (list) =>
  list.filter((r) => r.status === "admitted" && r.inputs.spot && r.payment?.spot_issues?.length);

export const exportSpotExceptionsXlsx = (list, filename) => {
  const wb = XLSX.utils.book_new();
  addSheet(wb, list.map((r) => ({
    "Receipt No": r.receipt_no,
    "Date of Enquiry": r.inputs.enquiryDate,
    "Child Name": r.inputs.childName,
    "Parent Name": r.inputs.parentName,
    Contact: r.inputs.contact,
    Grade: r.inputs.grade,
    "Adm Counsellor": r.inputs.counselorName,
    "Form Fee Paid": r.form_fee?.amount ?? "",
    "Form Fee Date": r.form_fee?.date || "",
    "Admission Fee Paid": r.payment.amount,
    "Admission Payment Date": r.payment.date,
    "Total Discount": r.computed.totalDiscount,
    "Rule Broken": r.payment.spot_issues.join("; "),
    Reason: r.payment.spot_note || "",
    "Admin Decision": r.payment.spot_decision?.decision || "pending",
    "Admin Comment": r.payment.spot_decision?.comment || "",
    "Decided By": r.payment.spot_decision?.by || "",
  })), "Spot Exceptions");
  XLSX.writeFile(wb, filename);
};

export const exportReceiptsXlsx = (receipts, filename) => {
  const wb = XLSX.utils.book_new();
  addSheet(wb, receipts.map(toRow), "Receipts");
  XLSX.writeFile(wb, filename);
};


export const exportRangeXlsx = (receipts, gradeOrder, filename) => {
  const wb = XLSX.utils.book_new();
  addSheet(wb, [...receipts].sort((a, b) => a.created_at.localeCompare(b.created_at)).map(toRow), "Receipts");
  const byDay = groupBy(receipts, (r) => localDay(r.created_at));
  addSheet(wb, [...Object.keys(byDay).sort().map((d) => ({ Date: d, ...totals(byDay[d]) })), { Date: "TOTAL", ...totals(receipts) }], "Daily Totals");
  const byGrade = groupBy(receipts, (r) => r.inputs.grade);
  addSheet(wb, [...gradeOrder.filter((g) => byGrade[g]).map((g) => ({ Grade: g, ...totals(byGrade[g]) })), { Grade: "TOTAL", ...totals(receipts) }], "By Grade");
  const cats = [["Spot", (i) => i.spot], ["Revisit", (i) => i.revisit], ...SUBCATS.map((s) => [s.label, (i) => i.sub?.[s.key]])];
  addSheet(wb, cats.map(([l, f]) => ({ "Category / Sub Category": l, ...totals(receipts.filter((r) => f(r.inputs))) })), "By Category");
  addSheet(wb, leaderboard(receipts).map((l) => ({
    Counsellor: l.name, Receipts: l.receipts, Admitted: l.admitted, "Conversion %": Math.round((l.admitted / l.receipts) * 100),
    "Due at Admission": l.atAdmission, "Total Discount": l.discount, "Avg Discount": Math.round(l.discount / l.receipts),
  })), "By Counsellor");
  XLSX.writeFile(wb, filename);
};

export const exportTeamXlsx = (team, daily, label, filename) => {
  const wb = XLSX.utils.book_new();
  const tot = (k) => team.reduce((s, t) => s + t[k], 0);
  const rows = team.map((t) => ({
    Counsellor: t.name, "Counselling Done": t.counselling, "Admissions Closed": t.admissions,
    "Conversion %": t.conversion ?? "", "Follow-ups Done": t.followups, "Amount Collected": t.collected,
  }));
  const c = tot("counselling"), a = tot("admissions");
  rows.push({ Counsellor: "TOTAL", "Counselling Done": c, "Admissions Closed": a, "Conversion %": c ? Math.round((a * 100) / c) : "",
    "Follow-ups Done": tot("followups"), "Amount Collected": tot("collected") });
  const ws = XLSX.utils.json_to_sheet([]);
  XLSX.utils.sheet_add_aoa(ws, [[`Team Performance — ${label}`], []]);
  XLSX.utils.sheet_add_json(ws, rows, { origin: "A3" });
  ws["!cols"] = Object.keys(rows[0]).map((k) => ({ wch: Math.max(14, k.length + 2) }));
  XLSX.utils.book_append_sheet(wb, ws, "By Counsellor");
  if (daily?.length) {
    addSheet(wb, daily.map((d) => ({ Date: d.date, "Counselling Done": d.counselling, "Admissions Closed": d.admissions, "Follow-ups Done": d.followups })), "Day-wise");
  }
  XLSX.writeFile(wb, filename);
};
